import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@nexus/ui";
import type { SortState } from "@/features/identity-hub/lib/sort";

interface SortHeaderProps<K extends string> {
  id: K;
  label: string;
  sort: SortState<K>;
  onSort: (key: K) => void;
  className?: string;
}

/**
 * Sortable `<th>` — label + chevron that flips between asc/desc, with a
 * faint hover-preview chevron on inactive columns. Requires a `group`
 * class on the ancestor `<tr>` for the hover preview to activate, and
 * relies on the header row (not this cell) to set text typography —
 * matches the existing pattern where `<thead><tr>` carries the shared
 * `text-xs font-medium text-muted-foreground` styling for every column.
 * Single source for the `SortHeader` copy previously duplicated across
 * the People, Roles, and Invites tables.
 */
export function SortHeader<K extends string>({ id, label, sort, onSort, className }: SortHeaderProps<K>) {
  const active = sort.key === id;
  return (
    <th
      scope="col"
      className={cn("whitespace-nowrap px-3 py-2.5 text-left font-normal", className)}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        className="inline-flex cursor-pointer select-none items-center gap-1 transition-colors hover:text-foreground"
        onClick={() => onSort(id)}
      >
        {label}
        {active ? (
          sort.dir === "asc" ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-40" />
        )}
      </button>
    </th>
  );
}
